/**
 * Substrate-v2 frame walker.
 *
 * Builds a `Map<ref, frameSelector>` for every element ref inside a sub-frame
 * (iframe). Substrate-v2 capture appends `[~frame=<selector>]` to those lines
 * via {@link appendV2FrameSelectors} so `buildLocator` knows which iframe
 * scope to enter at replay (`page.frameLocator(selector).getByRole(...)`).
 *
 * Strategy:
 *   1. Take the captured page-level YAML and pull out every `[ref=fNeM]`
 *      token — the `f<frameIndex>e<n>` prefix is how Playwright tags
 *      sub-frame refs in `mode: 'ai'`. (We don't enumerate from DOM
 *      because Playwright stores ref ids on `element._ariaRef` in its
 *      isolated world, not as a real `aria-ref` HTML attribute — a
 *      `frame.evaluate(() => …querySelectorAll('[aria-ref]'))` returns 0.)
 *   2. For each sub-frame, derive a CSS selector for the iframe element
 *      via `frame.frameElement()` (id / name / class / positional).
 *   3. Probe each candidate `f<N>e<M>` ref against the frame via the
 *      `aria-ref=` selector engine; whichever frame resolves the ref
 *      owns it.
 *   4. Cross-origin frames refuse `evaluate` — catch and skip; those refs
 *      stay un-tagged and `buildLocator` falls back to main-frame behavior.
 *
 * Read-only. No DOM mutation.
 */

import type { Frame, Page } from 'playwright';
import type { BrowserLogger } from '../logger.js';
import { encodeV2Value } from '../snapshot-analyzer.js';

const REF_PATTERN = /\[ref=(\w+)\]/;

/**
 * Walk every sub-frame on the page. Returns `Map<ref, frameSelector>` for
 * every element with an `aria-ref` attribute that lives inside an iframe.
 *
 * Errors per-frame are caught and logged at debug level; the walker
 * always returns a (possibly partial) map rather than throwing.
 */
export async function collectFrameSelectors(
  page: Page,
  yaml: string,
  logger: BrowserLogger
): Promise<Map<string, string>> {
  const result = new Map<string, string>();
  // Pull every sub-frame ref (`f<N>e<M>`) out of the page-level snapshot.
  // No DOM enumeration: Playwright's ref ids only live on
  // `element._ariaRef` in its isolated world, so we can't `querySelectorAll`
  // for them.
  const subFrameRefs = [...new Set(
    [...yaml.matchAll(/\[ref=(f\d+e\d+)\]/g)].map((m) => m[1])
  )];
  if (subFrameRefs.length === 0) return result;

  const frames = page.frames();
  for (const frame of frames) {
    if (frame === page.mainFrame()) continue;
    try {
      const selector = await deriveFrameSelector(frame);
      if (!selector) continue;
      // Probe each candidate ref against this frame via the selector
      // engine. The frame that resolves the ref owns it.
      const owned = await Promise.all(
        subFrameRefs.map(async (ref) => {
          if (result.has(ref)) return null;
          const handle = await frame.$(`aria-ref=${ref}`).catch(() => null);
          if (handle) {
            await handle.dispose().catch(() => {});
            return ref;
          }
          return null;
        })
      );
      for (const ref of owned) {
        if (ref && !result.has(ref)) result.set(ref, selector);
      }
    } catch (err) {
      logger.debug('[FrameWalker] Skipping frame', {
        url: frame.url(),
        error: err instanceof Error ? err.message : String(err),
      });
    }
  }

  return result;
}

/**
 * Derive a CSS selector for the iframe element backing this frame.
 * Returns null when the iframe element is detached or not accessible.
 *
 * Selector preference (matches v1 element-inspection.ts):
 *   1. `iframe#<id>` when id present
 *   2. `iframe[name="<name>"]` when name attr present
 *   3. `iframe.<first-class>` when class present
 *   4. `iframe:nth-of-type(N)` positional fallback
 *   5. `iframe` last resort
 */
async function deriveFrameSelector(frame: Frame): Promise<string | null> {
  let frameElement;
  try {
    frameElement = await frame.frameElement();
  } catch {
    return null;
  }
  if (!frameElement) return null;

  return frameElement.evaluate((el): string => {
    if (!(el instanceof HTMLIFrameElement)) return 'iframe';
    if (el.id) return `iframe#${el.id}`;
    if (el.name) return `iframe[name="${el.name}"]`;
    if (typeof el.className === 'string' && el.className.trim()) {
      const firstClass = el.className.split(/\s+/).find(Boolean);
      if (firstClass) return `iframe.${firstClass}`;
    }
    const siblings = el.parentElement?.querySelectorAll('iframe');
    if (siblings && siblings.length > 1) {
      const index = Array.from(siblings).indexOf(el);
      return `iframe:nth-of-type(${index + 1})`;
    }
    return 'iframe';
  });
}

/**
 * Append `[~frame=<selector>]` (percent-encoded) to each ref'd line whose
 * ref appears in the frame map. Idempotent: lines that already carry
 * `[~frame=...]` are not re-tagged.
 *
 * Pure string transform — no DOM access.
 */
export function appendV2FrameSelectors(
  yaml: string,
  frameByRef: ReadonlyMap<string, string>
): string {
  if (frameByRef.size === 0) return yaml;
  const lines = yaml.split('\n');
  for (let i = 0; i < lines.length; i++) {
    const m = lines[i].match(REF_PATTERN);
    if (!m) continue;
    const selector = frameByRef.get(m[1]);
    if (!selector) continue;
    if (lines[i].includes('[~frame=')) continue;
    lines[i] = appendAttrToLine(lines[i], `[~frame=${encodeV2Value(selector)}]`);
  }
  return lines.join('\n');
}

/**
 * Insert a synthetic attr segment before any trailing `:` (children marker)
 * or `: value` (textbox value), so YAML structure is preserved. Mirrors the
 * helper in `snapshot-substrate-v2.ts`.
 */
function appendAttrToLine(line: string, segment: string): string {
  const m = line.match(/^(.*?\])(:.*)?$/);
  if (m && m[2]) return `${m[1]}${segment}${m[2]}`;
  return `${line}${segment}`;
}
